import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Send, MapPin, Phone, Clock, CheckCircle, 
  MessageSquare, User, Mail, Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import Layout from '../layout/Layout';
import { contactAPI } from '../../lib/api';

const ContactPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    sujet: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await contactAPI.submit({
        ...formData,
        email: 'formulaire-contact@site.fr', // Pas d'email visible
      });
      setSubmitted(true);
      toast({ 
        title: 'Message envoyé !', 
        description: 'Nous vous recontacterons rapidement.'
      });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: 'Une erreur est survenue. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2C3C65] mb-4">Message envoyé !</h2>
            <p className="text-[#6777A1] mb-6">
              Merci de nous avoir contactés. Notre équipe vous répondra dans les plus brefs délais.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#1e4a9a] transition-colors"
            >
              Retour à l'accueil
            </a>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50" data-testid="contact-page">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Contactez-nous
              </h1>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Une question ? Besoin d'informations ? Notre équipe est à votre écoute
              </p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Colonne gauche - Infos + Map */}
            <div className="space-y-8">
              {/* Carte de contact */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden"
              >
                {/* Google Maps */}
                <div className="h-64 w-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2889.0547!2d4.9877!3d43.5147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b60d3e1c8d4d4f%3A0x8c5a5a5a5a5a5a5a!2s9%20All%C3%A9e%20Jean%20Jaur%C3%A8s%2C%2013800%20Istres!5e0!3m2!1sfr!2sfr!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localisation The English Speaking Center"
                  />
                </div>
                
                {/* Informations */}
                <div className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#285BBB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-[#285BBB]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3C65]">Adresse</h3>
                      <p className="text-[#6777A1]">9 Allée Jean Jaurès</p>
                      <p className="text-[#6777A1]">13800 Istres</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#ff3a2d]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-[#ff3a2d]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3C65]">Téléphone</h3>
                      <a 
                        href="tel:+33626716022" 
                        className="text-[#285BBB] font-medium hover:underline text-lg"
                      >
                        06 26 71 60 22
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3C65]">Horaires</h3>
                      <p className="text-[#6777A1]">Lundi - Vendredi : 9h00 - 19h00</p>
                      <p className="text-[#6777A1]">Samedi : 9h00 - 12h00</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Infos supplémentaires */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-2xl p-6 text-white"
              >
                <h3 className="font-bold text-lg mb-4">Évaluation gratuite</h3>
                <p className="text-blue-100 mb-4">
                  Vous souhaitez connaître votre niveau d'anglais ? 
                  Demandez un cours d'évaluation gratuit pour définir votre niveau 
                  et trouver la formation adaptée.
                </p>
                <a 
                  href="/demande-info"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#285BBB] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Demander une évaluation
                </a>
              </motion.div>
            </div>

            {/* Colonne droite - Formulaire */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#285BBB] rounded-xl flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#2C3C65]">Envoyez-nous un message</h2>
                  <p className="text-[#6777A1] text-sm">Nous vous répondrons sous 24h</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                      Nom *
                    </label>
                    <Input
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      placeholder="Votre nom"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                      Prénom *
                    </label>
                    <Input
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      placeholder="Votre prénom"
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                    Téléphone *
                  </label>
                  <Input
                    type="tel"
                    required
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="06 XX XX XX XX"
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                    Sujet *
                  </label>
                  <select
                    required
                    value={formData.sujet}
                    onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="information">Demande d'information</option>
                    <option value="evaluation">Cours d'évaluation gratuit</option>
                    <option value="inscription">Question sur l'inscription</option>
                    <option value="cpf">Formation CPF</option>
                    <option value="entreprise">Formation entreprise</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                    Message *
                  </label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Décrivez votre demande..."
                    rows={5}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#ff3a2d] hover:bg-[#e6352a] text-white font-bold text-lg h-14"
                  data-testid="submit-contact"
                >
                  {loading ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Envoyer mon message
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-[#6777A1] text-center">
                  En soumettant ce formulaire, vous acceptez que vos données soient utilisées 
                  pour vous recontacter. Aucune donnée n'est partagée avec des tiers.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;
